import { Injectable, computed, inject, signal } from '@angular/core';
import { MealPlanModel } from '@models/meal-plan/meal-plan.model';
import { OrderCartModel } from '@models/order/order-cart.model';
import { MealPlanService } from './meal-plan.service';
import { OrderService } from './order.service';
import { UserCompanyDataService } from './user-company-data.service';

const STORAGE_KEY = 'invite_order_state';

export interface InviteParticipant {
  cart: OrderCartModel;
  mealPlan: MealPlanModel | null;
}

interface InviteOrderState {
  targetInviteCount: number;
  participants: (InviteParticipant | null)[];
  drafts: (InviteParticipant | null)[];
  activeIndex: number;
  comment: string;
}

function emptyState(): InviteOrderState {
  return {
    targetInviteCount: 0,
    participants: [null],
    drafts: [null],
    activeIndex: 0,
    comment: '',
  };
}

function resized<T>(list: T[], length: number): T[] {
  const next = list.slice(0, length);
  while (next.length < length) next.push(null as T);
  return next;
}

@Injectable({ providedIn: 'root' })
export class InviteOrderService {
  private readonly userCompanyDataService = inject(UserCompanyDataService);
  private readonly orderService = inject(OrderService);
  private readonly mealPlanService = inject(MealPlanService);

  private readonly state = signal<InviteOrderState>(this.#restore());

  readonly targetInviteCount = computed(() => this.state().targetInviteCount);
  readonly participants = computed(() => this.state().participants);
  readonly drafts = computed(() => this.state().drafts);
  readonly activeIndex = computed(() => this.state().activeIndex);
  readonly guestCount = computed(() => this.state().targetInviteCount);
  readonly comment = computed(() => this.state().comment);

  readonly maxInvite = computed(
    () => this.userCompanyDataService.currentUserCompanyData()?.max_invite ?? 0,
  );

  readonly canInvite = computed(
    () =>
      this.userCompanyDataService.currentUserCompanyData()?.is_can_book === true &&
      this.maxInvite() > 0,
  );

  readonly isComplete = computed(() => this.state().participants.every((p) => p !== null));

  readonly canGoToPrevious = computed(() => this.state().activeIndex > 0);

  readonly canGoToNext = computed(() => {
    const { activeIndex, drafts } = this.state();
    return activeIndex < drafts.length - 1 && drafts[activeIndex + 1] !== null;
  });

  readonly isAtLastFilledParticipant = computed(() => {
    const { activeIndex, participants } = this.state();
    return activeIndex === participants.length - 1 && participants[activeIndex] !== null;
  });

  readonly mergedCart = computed<OrderCartModel | null>(() => {
    const carts = this.state()
      .participants.filter((p): p is InviteParticipant => p !== null)
      .map((p) => p.cart);
    if (carts.length === 0) return null;
    return {
      items: carts.flatMap((cart) => cart.items),
      formulaPrice: carts.reduce((sum, cart) => sum + cart.formulaPrice, 0),
      supplementTotal: carts.reduce((sum, cart) => sum + cart.supplementTotal, 0),
      totalPrice: carts.reduce((sum, cart) => sum + cart.totalPrice, 0),
      isMealPlanMode: carts[0].isMealPlanMode,
    };
  });

  setInviteCount(count: number, comment = ''): void {
    const clamped = Math.min(Math.max(Math.trunc(count), 0), this.maxInvite());
    this.#update((current) => {
      const nextLength = clamped + 1;
      return {
        ...current,
        targetInviteCount: clamped,
        participants: resized(current.participants, nextLength),
        drafts: resized(current.drafts, nextLength),
        activeIndex: Math.min(current.activeIndex, nextLength - 1),
        comment: clamped > 0 ? comment : '',
      };
    });
  }

  submitCurrentCart(cart: OrderCartModel, mealPlan: MealPlanModel | null): void {
    this.#update((current) => {
      const participant: InviteParticipant = { cart, mealPlan };
      const participants = [...current.participants];
      const drafts = [...current.drafts];
      participants[current.activeIndex] = participant;
      drafts[current.activeIndex] = participant;
      return { ...current, participants, drafts };
    });
  }

  saveDraft(cart: OrderCartModel, mealPlan: MealPlanModel | null): void {
    this.state.update((current) => {
      const drafts = [...current.drafts];
      drafts[current.activeIndex] = { cart, mealPlan };
      return { ...current, drafts };
    });
    this.#schedulePersist();
  }

  advanceToNextPending(): number | null {
    const nextIndex = this.state().participants.findIndex((p) => p === null);
    if (nextIndex === -1) return null;
    this.#update((current) => ({ ...current, activeIndex: nextIndex }));
    return nextIndex;
  }

  goToIndex(index: number): InviteParticipant | null {
    const { participants, drafts } = this.state();
    if (index < 0 || index >= participants.length) return null;
    this.#update((current) => ({ ...current, activeIndex: index }));
    return drafts[index] ?? participants[index];
  }

  goToPrevious(): InviteParticipant | null {
    return this.goToIndex(this.state().activeIndex - 1);
  }

  goToNext(): InviteParticipant | null {
    return this.goToIndex(this.state().activeIndex + 1);
  }
  restoreParticipant(participant: InviteParticipant | null): void {
    if (participant) {
      this.orderService.save(participant.cart);
      if (participant.mealPlan) {
        this.mealPlanService.setMealPlan(participant.mealPlan);
      } else {
        this.mealPlanService.clearMealPlan();
      }
    } else {
      this.orderService.clear();
      this.mealPlanService.clearMealPlan();
    }
  }

  restoreHostCart(cart: OrderCartModel, mealPlan: MealPlanModel | null): void {
    const participant: InviteParticipant = { cart, mealPlan };
    this.state.set({
      targetInviteCount: 0,
      participants: [participant],
      drafts: [participant],
      activeIndex: 0,
      comment: '',
    });
    this.#persist();
  }

  reset(): void {
    this.state.set(emptyState());
    sessionStorage.removeItem(STORAGE_KEY);
  }

  #update(updater: (current: InviteOrderState) => InviteOrderState): void {
    this.state.set(updater(this.state()));
    this.#persist();
  }

  #persistTimeoutId: ReturnType<typeof setTimeout> | null = null;

  #schedulePersist(): void {
    if (this.#persistTimeoutId !== null) return;
    this.#persistTimeoutId = setTimeout(() => {
      this.#persistTimeoutId = null;
      this.#persist();
    }, 400);
  }

  #persist(): void {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.state()));
  }

  #restore(): InviteOrderState {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return emptyState();
      const parsed = JSON.parse(raw) as Partial<InviteOrderState>;
      if (!Array.isArray(parsed.participants)) return emptyState();
      return {
        targetInviteCount: parsed.targetInviteCount ?? 0,
        participants: parsed.participants,
        drafts: Array.isArray(parsed.drafts) ? parsed.drafts : parsed.participants,
        activeIndex: parsed.activeIndex ?? 0,
        comment: typeof parsed.comment === 'string' ? parsed.comment : '',
      };
    } catch {
      return emptyState();
    }
  }
}
